import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import SiteCard from "@/components/SiteCard";
import DayHeader from "@/components/DayHeader";
import UnassignedPanel from "@/components/UnassignedPanel";
import BaseListsDialog from "@/components/BaseListsDialog";
import SearchableSelect from "@/components/SearchableSelect";
import { useDayActions } from "@/hooks/useDayActions";
import { useDragAutoscroll, useSiteReorder } from "@/hooks/useDayDnd";
import { Copy } from "lucide-react";

const enc = encodeURIComponent;

function NewSiteCard({ masterSites, onAdd }) {
  return (
    <div className="print-hide flex flex-col items-start justify-start border border-dashed border-slate-300 rounded-sm p-4">
      <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Nuovo cantiere nel giorno</p>
      <SearchableSelect options={masterSites} allowFree onSelect={onAdd}
        placeholder="Cerca o digita nome cantiere..." testId="new-site" className="w-full" />
    </div>
  );
}

function DayGrid({ data, stats, monthKey, dayNum, master, onChanged, onAddSite, onSiteMove, filter }) {
  return (
    <div className="daily-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
      {data.sites.map((s) => (
        <SiteCard key={s.name} site={s} stats={stats} monthKey={monthKey} dayNum={dayNum}
          baseList={data.base_list} subOptions={master.subcontractors} onChanged={onChanged} onSiteMove={onSiteMove} filter={filter} />
      ))}
      <NewSiteCard masterSites={master.sites} onAdd={onAddSite} />
    </div>
  );
}

function CopyHint() {
  return (
    <div data-testid="copy-hint" className="print-hide mx-4 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900 flex items-center gap-2">
      <Copy className="w-3.5 h-3.5" /> Giorno vuoto: usa "Copia programmazione" per importare la programmazione da un altro giorno in stato blu.
    </div>
  );
}

function useMasterData(monthKey, dayNum) {
  const [master, setMaster] = useState({ sites: [], subcontractors: [] });
  useEffect(() => {
    api.get("/master").then((r) => setMaster(r.data)).catch(() => {});
    // api è un import di modulo stabile
  }, [monthKey, dayNum]); // eslint-disable-line react-hooks/exhaustive-deps
  return master;
}

export default function DayView({ monthKey, dayNum, refreshKey, numDays, months }) {
  const [data, setData] = useState(null);
  const [showBaseList, setShowBaseList] = useState(false);
  const [personFilter, setPersonFilter] = useState("");
  const master = useMasterData(monthKey, dayNum);

  const load = useCallback(async () => {
    const res = await api.get(`/months/${enc(monthKey)}/days/${dayNum}`);
    setData(res.data);
    // api ed enc sono stabili (modulo)
  }, [monthKey, dayNum]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const { copyPrevious, saveBaseLists, addSite } = useDayActions(monthKey, dayNum, load);
  useDragAutoscroll();
  const moveSite = useSiteReorder(monthKey, dayNum, data, setData, load);

  if (!data) return <div className="p-6 text-sm text-slate-500" data-testid="day-loading">Caricamento...</div>;

  const stats = data.stats;
  const hasData = data.sites.some((s) => s.assignments.length || s.subcontractors.length);

  return (
    <div className="flex flex-col lg:flex-row">
      <main className="print-area flex-1 min-w-0">
        <DayHeader data={data} stats={stats} monthKey={monthKey} dayNum={dayNum} numDays={numDays} months={months}
          onCopyPrevious={copyPrevious}
          onEditBaseList={() => setShowBaseList(true)}
          personFilter={personFilter}
          onPersonFilterChange={setPersonFilter} />

        {dayNum > 1 && !hasData && <CopyHint />}

        <DayGrid data={data} stats={stats} monthKey={monthKey} dayNum={dayNum}
          master={master} onChanged={load} onAddSite={addSite} onSiteMove={moveSite} filter={personFilter} />

        {/* blocco visibile solo in stampa/PDF: nominativi non assegnati ad alcun cantiere o ad ASSENZE/FERIE */}
        {stats.unassigned.length > 0 && (
          <div data-testid="print-unassigned" className="hidden print:block mx-4 mt-2 border border-slate-400 rounded-sm">
            <div className="bg-slate-100 px-3 py-1 text-sm font-bold uppercase tracking-tight border-b border-slate-300">
              Non assegnati ({stats.unassigned.length})
            </div>
            <div className="px-3 py-1.5 text-sm text-slate-800">
              {stats.unassigned.join("   ·   ")}
            </div>
          </div>
        )}
      </main>

      <UnassignedPanel stats={stats} filter={personFilter} />

      <BaseListsDialog open={showBaseList} onClose={() => setShowBaseList(false)} dayNum={dayNum}
        personnel={data.base_list} sites={data.base_sites} subcontractors={data.base_subcontractors}
        onSave={saveBaseLists} />
    </div>
  );
}
